import { useState, useCallback, useRef, useEffect } from 'react'
import { toast } from 'react-hot-toast'
import {
  XMarkIcon,
  ShieldCheckIcon,
  UserIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  UserPlusIcon,
  ClockIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline'
import { Button } from '@/components/common/Button'
import { ConfirmationModal } from '@/components/common/ConfirmationModal'
import { ShareAgentModal } from './ShareAgentModal'
import { AgentManagementService } from '@/services/api/services/AgentManagementService'
import { useAgentSharing } from '@/hooks/useAgentSharing'
import { useAuthStore } from '@/stores/authStore'
import type { Agent } from '@/services/api/models/Agent'
import type { PermissionResponse } from '@/services/api/models/PermissionResponse'
import type { InvitationResponse } from '@/services/api/models/InvitationResponse'

interface AgentPermissionsModalProps {
  isOpen: boolean
  onClose: () => void
  agent: Agent | null
  currentUserPermission?: 'owner' | 'read' | 'write'
  onPermissionsChange?: () => void
}

export function AgentPermissionsModal({
  isOpen,
  onClose,
  agent,
  currentUserPermission,
  onPermissionsChange
}: AgentPermissionsModalProps) {
  const [permissions, setPermissions] = useState<PermissionResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({})
  const [error, setError] = useState<string | null>(null)
  const [showShareModal, setShowShareModal] = useState(false)
  const [showRemoveModal, setShowRemoveModal] = useState(false)
  const [userToRemove, setUserToRemove] = useState<PermissionResponse | null>(null)
  const [effectivePermission, setEffectivePermission] = useState<'owner' | 'read' | 'write' | undefined>(currentUserPermission)
  
  // Pending invitations state
  const [pendingInvitations, setPendingInvitations] = useState<InvitationResponse[]>([])
  const [invitationsLoading, setInvitationsLoading] = useState(false)
  const [showRevokeModal, setShowRevokeModal] = useState(false)
  const [invitationToRevoke, setInvitationToRevoke] = useState<InvitationResponse | null>(null)

  const modalRef = useRef<HTMLDivElement>(null)
  const { getOutgoingInvitations, revokeInvitation } = useAgentSharing()
  const { user: currentUser } = useAuthStore()

  // Use effective permission which can be updated after ownership transfer
  const canManagePermissions = effectivePermission === 'owner' || effectivePermission === 'write'
  const canShare = effectivePermission === 'owner' || effectivePermission === 'write'

  const fetchPermissions = useCallback(async () => {
    if (!agent?.id) return

    try {
      setLoading(true)
      setError(null)
      
      const response = await AgentManagementService.listAgentPermissions({
        agentId: agent.id
      })
      
      setPermissions(response)
      
      // Check if current user is in the permissions list and update effective permission
      if (currentUser) {
        const currentUserPermissionInList = response.find(p => 
          p.user_id === currentUser.id || 
          p.user_email === currentUser.email ||
          p.username === currentUser.username
        )
        
        if (currentUserPermissionInList) {
          setEffectivePermission(currentUserPermissionInList.permission as 'owner' | 'read' | 'write')
        }
      }
    } catch (err: unknown) {
      console.error('Failed to fetch permissions:', err)
      setError(err instanceof Error ? err.message : 'Failed to load permissions')
    } finally {
      setLoading(false)
    }
  }, [agent?.id, currentUser])

  const fetchPendingInvitations = useCallback(async () => {
    if (!agent?.id || !canManagePermissions) return

    try {
      setInvitationsLoading(true)
      const invitations = await getOutgoingInvitations(agent.id)
      setPendingInvitations(invitations)
    } catch (err: unknown) {
      console.error('Failed to fetch pending invitations:', err)
      // Don't show error for invitations as it's not critical
    } finally {
      setInvitationsLoading(false)
    }
  }, [agent?.id, canManagePermissions, getOutgoingInvitations])

  useEffect(() => {
    if (isOpen && agent?.id) {
      // Reset effective permission to the prop value when modal opens
      setEffectivePermission(currentUserPermission)
      fetchPermissions()
      fetchPendingInvitations()
    }
  }, [isOpen, agent?.id, currentUserPermission, fetchPermissions, fetchPendingInvitations])

  // Handle escape key and backdrop clicks
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !showShareModal && !showRemoveModal && !showRevokeModal) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose, showShareModal, showRemoveModal, showRevokeModal])

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !showShareModal && !showRemoveModal && !showRevokeModal) {
      onClose()
    }
  }

  const handleUpdatePermission = async (permission: PermissionResponse, newPermission: 'read' | 'write') => {
    if (!agent?.id || !permission.user_id) return

    const loadingKey = `update-${permission.user_id}`
    setActionLoading(prev => ({ ...prev, [loadingKey]: true }))

    try {
      await AgentManagementService.updateAgentPermission({
        agentId: agent.id,
        userId: permission.user_id,
        requestBody: { permission: newPermission }
      })

      // Update the permission in the list
      setPermissions(prev => prev.map(p => 
        p.user_id === permission.user_id 
          ? { ...p, permission: newPermission }
          : p
      ))

      const displayName = permission.username || permission.user_email || permission.user_name
      toast.success(`Updated ${displayName}'s permission to ${newPermission}`)
      onPermissionsChange?.()
    } catch (err: unknown) {
      console.error('Failed to update permission:', err)
      toast.error(err instanceof Error ? err.message : 'Failed to update permission')
    } finally {
      setActionLoading(prev => {
        const { [loadingKey]: _, ...rest } = prev
        return rest
      })
    }
  }

  const handleRemovePermission = async () => {
    if (!agent?.id || !userToRemove?.user_id) return

    const loadingKey = `remove-${userToRemove.user_id}`
    setActionLoading(prev => ({ ...prev, [loadingKey]: true }))

    try {
      await AgentManagementService.removeAgentPermission({
        agentId: agent.id,
        userId: userToRemove.user_id
      })

      const wasOwner = userToRemove.permission === 'owner'
      const displayName = userToRemove.username || userToRemove.user_email || userToRemove.user_name
      
      // If ownership was transferred, refresh permissions to show the current user as the new owner
      if (wasOwner) {
        await fetchPermissions()
        toast.success(`Ownership transferred from ${displayName}. You are now the owner.`)
      } else {
        // For non-owner removals, just update the local state
        setPermissions(prev => prev.filter(p => p.user_id !== userToRemove.user_id))
        toast.success(`Removed ${displayName}'s access`)
      }
      
      onPermissionsChange?.()
      setShowRemoveModal(false)
      setUserToRemove(null)
    } catch (err: unknown) {
      console.error('Failed to remove permission:', err)
      toast.error(err instanceof Error ? err.message : 'Failed to remove access')
    } finally {
      setActionLoading(prev => {
        const { [loadingKey]: _, ...rest } = prev
        return rest
      })
    }
  }

  const handleRevokeInvitation = async () => {
    if (!agent?.id || !invitationToRevoke?.id) return

    const loadingKey = `revoke-${invitationToRevoke.id}`
    setActionLoading(prev => ({ ...prev, [loadingKey]: true }))

    try {
      await revokeInvitation(agent.id, invitationToRevoke.id)

      // Remove the invitation from the list
      setPendingInvitations(prev => prev.filter(inv => inv.id !== invitationToRevoke.id))

      toast.success(`Revoked invitation for ${invitationToRevoke.invitee_email}`)
      // Refresh counts/badges on parent (agent card) and local invitations
      onPermissionsChange?.()
      void fetchPendingInvitations()
      setShowRevokeModal(false)
      setInvitationToRevoke(null)
    } catch (err: unknown) {
      console.error('Failed to revoke invitation:', err)
      toast.error(err instanceof Error ? err.message : 'Failed to revoke invitation')
    } finally {
      setActionLoading(prev => {
        const { [loadingKey]: _, ...rest } = prev
        return rest
      })
    }
  }

  const getPermissionIcon = (permission: string) => {
    switch (permission) {
      case 'owner':
        return <ShieldCheckIcon className="w-4 h-4 text-yellow-500" />
      case 'write':
        return <PencilIcon className="w-4 h-4 text-green-500" />
      case 'read':
        return <EyeIcon className="w-4 h-4 text-blue-500" />
      default:
        return <UserIcon className="w-4 h-4 text-gray-400" />
    }
  }

  const getPermissionBadge = (permission: string) => {
    switch (permission) {
      case 'owner':
        return (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300 rounded-full">
            <ShieldCheckIcon className="w-3 h-3 mr-1" />
            Owner
          </span>
        )
      case 'write':
        return (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded-full">
            <PencilIcon className="w-3 h-3 mr-1" />
            Write
          </span>
        )
      case 'read':
        return (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-full">
            <EyeIcon className="w-3 h-3 mr-1" />
            Read
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300 rounded-full">
            Unknown
          </span>
        )
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const handleModalClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
  }, [])

  if (!isOpen || !agent) return null

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={handleBackdropClick}
      >
        <div
          ref={modalRef}
          className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700"
          onClick={handleModalClick}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/20">
                <ShieldCheckIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Agent Permissions
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Manage access to "{agent.name}"
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {canShare && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => { setShowShareModal(true) }}
                  className="flex items-center space-x-2"
                >
                  <UserPlusIcon className="w-4 h-4" />
                  <span>Invite Users</span>
                </Button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <ExclamationTriangleIcon className="w-8 h-8 text-red-500 mx-auto mb-2" />
                  <p className="text-sm text-red-600 dark:text-red-400 mb-2">{error}</p>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={fetchPermissions}
                  >
                    Try Again
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {/* Pending Invitations Section */}
                {canManagePermissions && (
                  <div className="border-b border-gray-200 dark:border-gray-700">
                    <div className="p-4 bg-gray-50 dark:bg-gray-900/50">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center">
                        <ClockIcon className="w-4 h-4 mr-2 text-yellow-500" />
                        Pending Invitations
                        {pendingInvitations.length > 0 && (
                          <span className="ml-2 inline-flex items-center justify-center px-1 py-0.5 text-[10px] font-bold text-white bg-yellow-500 rounded-full min-w-[16px] h-[16px]">
                            {pendingInvitations.length}
                          </span>
                        )}
                        {invitationsLoading && (
                          <div className="ml-2 w-4 h-4 animate-spin border-2 border-yellow-500 border-t-transparent rounded-full" />
                        )}
                      </h4>
                      {pendingInvitations.length === 0 ? (
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          No pending invitations
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {pendingInvitations.map((invitation) => (
                            <div key={invitation.id} className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                              <div className="flex items-center space-x-3">
                                <EnvelopeIcon className="w-4 h-4 text-blue-500" />
                                <div>
                                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                                    {invitation.invitee_email}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {invitation.permission === 'write' ? 'Write Access' : 'Read Access'}
                                    {invitation.created_at && (
                                      <span> • Sent {formatDate(invitation.created_at)}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  setInvitationToRevoke(invitation)
                                  setShowRevokeModal(true)
                                }}
                                disabled={!!(invitation.id && actionLoading[`revoke-${invitation.id}`])}
                                className="p-1 text-red-600 hover:text-red-700 dark:text-red-400 disabled:opacity-50"
                                title="Revoke invitation"
                              >
                                {invitation.id && actionLoading[`revoke-${invitation.id}`] ? (
                                  <div className="w-4 h-4 animate-spin border-2 border-current border-t-transparent rounded-full" />
                                ) : (
                                  <XMarkIcon className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Current Permissions Section */}
                {permissions.length === 0 ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                      <UserIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        No permissions found
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    <div className="p-4 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
                        <ShieldCheckIcon className="w-4 h-4 mr-2 text-blue-500" />
                        Current Access
                      </h4>
                    </div>
                    {permissions.map((permission) => {
                      const isCurrentUser = currentUser && (
                        permission.user_id === currentUser.id || 
                        permission.user_email === currentUser.email ||
                        permission.username === currentUser.username
                      )
                      
                      return (
                      <div key={permission.user_id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                              {getPermissionIcon(permission.permission || 'read')}
                            </div>
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                                {permission.username || permission.user_email || permission.user_name}
                                {isCurrentUser && (
                                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-full">
                                    You
                                  </span>
                                )}
                              </h4>
                              {permission.user_name && !isCurrentUser && (
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  {permission.user_name}
                                </p>
                              )}
                              <div className="flex items-center space-x-2 mt-1">
                                {getPermissionBadge(permission.permission || 'read')}
                                {permission.granted_by_name && (
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    by {permission.granted_by_name}
                                  </span>
                                )}
                              </div>
                              {permission.created_at && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  Added {formatDate(permission.created_at)}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          {canManagePermissions && (
                            <div className="flex items-center space-x-2">
                              {/* Only show permission dropdown for non-owners and not for current user */}
                              {permission.permission !== 'owner' && !isCurrentUser && (
                                <select
                                  value={permission.permission || 'read'}
                                  onChange={(e) => { const v = e.target.value; handleUpdatePermission(permission, v === 'write' ? 'write' : 'read') }}
                                  disabled={actionLoading[`update-${permission.user_id}`]}
                                  className="text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-2 py-1"
                                >
                                  <option value="read">Read</option>
                                  <option value="write">Write</option>
                                </select>
                              )}

                              {/* Remove Button - hide for current user */}
                              {!isCurrentUser && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setUserToRemove(permission)
                                    setShowRemoveModal(true)
                                  }}
                                  disabled={actionLoading[`remove-${permission.user_id}`]}
                                  className="p-1 text-red-600 hover:text-red-700 dark:text-red-400 disabled:opacity-50"
                                  title={permission.permission === 'owner' ? 'Remove owner (you will become the new owner)' : 'Remove access'}
                                >
                                  {actionLoading[`remove-${permission.user_id}`] ? (
                                    <div className="w-4 h-4 animate-spin border-2 border-current border-t-transparent rounded-full" />
                                  ) : (
                                    <TrashIcon className="w-4 h-4" />
                                  )}
                                </button>
                              )}
                            </div>
                          )}
                      </div>
                    </div>
                    )
                    })}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center p-6 border-t border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {permissions.length} user{permissions.length !== 1 ? 's' : ''} with access
            </div>
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>

      {/* Share Agent Modal */}
      <ShareAgentModal
        isOpen={showShareModal}
        onClose={() => { setShowShareModal(false) }}
        agent={agent}
        onInvitationSent={() => {
          setShowShareModal(false)
          // Refresh both permissions and pending invitations
          fetchPermissions()
          fetchPendingInvitations()
          // Notify parent so agent list badges/counts refresh
          onPermissionsChange?.()
        }}
      />

      {/* Remove Confirmation Modal */}
      <ConfirmationModal
        isOpen={showRemoveModal}
        onClose={() => {
          setShowRemoveModal(false)
          setUserToRemove(null)
        }}
        onConfirm={handleRemovePermission}
        title={userToRemove?.permission === 'owner' ? 'Transfer Ownership' : 'Remove Access'}
        message={(() => {
          const displayName = userToRemove?.username || userToRemove?.user_email || userToRemove?.user_name
          return userToRemove?.permission === 'owner' 
            ? `Are you sure you want to remove ${displayName} as the owner of this agent? You will become the new owner.`
            : `Are you sure you want to remove ${displayName}'s access to this agent? They will no longer be able to use this agent.`
        })()}
        confirmText={userToRemove?.permission === 'owner' ? 'Become Owner' : 'Remove Access'}
        cancelText="Cancel"
        variant="danger"
        isDangerous={true}
      />

      {/* Revoke Invitation Confirmation Modal */}
      <ConfirmationModal
        isOpen={showRevokeModal}
        onClose={() => {
          setShowRevokeModal(false)
          setInvitationToRevoke(null)
        }}
        onConfirm={handleRevokeInvitation}
        title="Revoke Invitation"
        message={`Are you sure you want to revoke the invitation for ${invitationToRevoke?.invitee_email}? They will not be able to accept this invitation.`}
        confirmText="Revoke Invitation"
        cancelText="Cancel"
        variant="danger"
        isDangerous={true}
      />
    </>
  )
}